import {CRecordClass} from 'app/modules/discovery/core/newmodels/_base'
import {queryClient} from 'app/react-query'
import {showDiff} from 'utilities/stringification'
import {isJsonData, JsonData} from 'utilities/types'

export class ApiReversibleUpdate {
  queryKey
  recordClass
  original?: JsonData
  originalTs?: number
  updated?: JsonData
  updatedTs?: number
  updateFunc?: (original?: JsonData) => JsonData
  state: 'ready' | 'applied' | 'committed' | 'reverted'

  constructor(
    queryKey: string[],
    recordClass: CRecordClass,
    updateFunc?: (original?: JsonData) => JsonData
  ) {
    this.queryKey = queryKey
    this.recordClass = recordClass
    this.original = undefined
    this.originalTs = undefined
    this.updated = undefined
    this.updatedTs = undefined
    this.updateFunc = updateFunc
    this.state = 'ready'
  }

  describe() {
    console.log(
      `${this.recordClass.name} -> ${this.queryKey.join(',')}\n${showDiff(
        this.original,
        this.updated
      ).join('\n')}`
    )
  }

  apply(updateFunc?: (original?: JsonData) => JsonData) {
    if (this.state !== 'ready') {
      throw new Error(`Cannot commit() on an update in state "${this.state}".`)
    }
    const func = updateFunc ?? this.updateFunc
    if (!func) {
      throw new Error(`ReversibleUpdate requires an updateFunc.`)
    }
    this.original = queryClient.getQueryData(this.queryKey)
    if (!isJsonData(this.original)) {
      throw new Error(`Commit failed: Expected original to be JSON data, got: ${this.original}`)
    }
    this.originalTs = queryClient.getQueryState(this.queryKey)?.dataUpdatedAt
    this.updated = func(this.original)
    this.updatedTs = Date.now()

    queryClient.setQueryData(this.queryKey, this.updated, {updatedAt: this.updatedTs})
    this.state = 'applied'
  }

  commit() {
    if (this.state === 'reverted') {
      throw new Error(`Cannot commit a reverted update.`)
    }
    if (this.state !== 'applied') {
      console.log(`Committing update in state "${this.state}": No effect.`)
      return
    }
    this.state = 'committed'
  }

  rollback() {
    if (this.state !== 'applied') {
      console.log(`Rolling back update in state "${this.state}": No effect.`)
      return
    }
    this.state = 'reverted'
    if (this.updated === undefined || this.updatedTs === undefined) {
      return
    }
    const currentTs = queryClient.getQueryState(this.queryKey)?.dataUpdatedAt
    if (currentTs === undefined || currentTs < this.updatedTs) {
      console.log(`WARNING: Expected query timestamp to be >= ${this.updatedTs}, got: ${currentTs}`)
    }
    if (currentTs !== undefined && currentTs > this.updatedTs) {
      console.log(
        `Record ${this.queryKey} was updated at ${currentTs}, superseding failed update at timestamp ${this.updatedTs}.`
      )
      return
    }
    queryClient.setQueryData(this.queryKey, this.original, {
      updatedAt: this.originalTs,
    })
    console.log(`Rolled back record ${this.queryKey} to ${this.originalTs}.`)
  }
}

export class ApiUpdateBatch {
  updates
  _onCommit

  constructor(
    updates: ApiReversibleUpdate[],
    opts?: {autocommit?: boolean; onCommit?: () => void}
  ) {
    const {autocommit, onCommit} = opts ?? {}
    this.updates = updates
    this._onCommit = onCommit ?? (() => {})

    if (autocommit) {
      this.apply()
      this.commit()
    }
  }

  apply() {
    this.updates.map((x) => x.apply())
  }

  commit() {
    this.updates.map((x) => x.commit())
    this._onCommit()
  }

  rollback() {
    this.updates.map((x) => x.rollback())
  }
}
